// Account/AdminCreateUser.tsx

import React, { useState } from "react";
import {
  VStack,
  Text,
  Input,
  Button,
  HStack,
  Pressable,
  CheckIcon,
  Select as NBSelect,
} from "native-base";
import { Eye, EyeOff } from "lucide-react-native";
import { useAPI } from "../../../../../Universal/API/API";
import { useAppContext } from "../../../../../Universal/API/Context-API/AppContext";
import { DeanteUser } from "./interfaces";
import PlatformSelect from "../../../../../Universal/Inputs/PlatformSelect";


interface AdminCreateUserProps {
  onUserCreated?: (user: DeanteUser) => void;
}



const AdminCreateUser: React.FC<AdminCreateUserProps> = ({
  onUserCreated,
}) => {
  const { sendFormDataPostRequest, getUtils } = useAPI();
  const { USER, OTHER_INFO_FIELDS: OF} = useAppContext();

  console.log('OF', OF)
  
const OTHER_INFO_FIELDS = React.useMemo(() =>OF ?? [] , [OF] ) 
  
  // Основные поля пользователя
  const [newLogin, setNewLogin] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [newDiscount, setNewDiscount] = useState<string>("0");
  const [newRole, setNewRole] = useState<"user" | "admin">("user");

  // other_info по ключам
  const [otherInfoValues, setOtherInfoValues] = useState<
    Record<string, string>
  >(
    () =>
      OTHER_INFO_FIELDS.reduce(
        (acc, field) => ({ ...acc, [field.key]: "" }),
        {} as Record<string, string>
      )
  );

  // UI состояния
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleChangeOtherInfo = (key: string, value: string) => {
    setOtherInfoValues((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleCreateUser = async () => {
    setError(null);
    setSuccess(null);

    if (!USER || USER.role !== "admin") {
      setError("Только администратор может создавать пользователей");
      return;
    }

    if (!newLogin || !newPassword) {
      setError("Логин и пароль обязательны");
      return;
    }

    const parsedDiscount = parseInt(newDiscount, 10);
    const safeDiscount = Number.isNaN(parsedDiscount) ? 0 : parsedDiscount;

    // Собираем объект other_info: { organization: "...", phone: "...", ... }
    const otherInfoObj: Record<string, string> = {};
    OTHER_INFO_FIELDS.forEach((field) => {
      const val = (otherInfoValues[field.key] || "").trim();
      if (val !== "") {
        otherInfoObj[field.key] = val;
      }
    });

    const otherInfoJSON =
      Object.keys(otherInfoObj).length > 0
        ? JSON.stringify(otherInfoObj)
        : null;

    setLoading(true);

    try {
      const formData = new FormData();
      formData.append("login", newLogin);
      formData.append("password", newPassword);
      formData.append("role", newRole);
      formData.append("access_token", USER.access_token); // admin access token
      formData.append("discount", String(safeDiscount));

      if (otherInfoJSON) {
        formData.append("other_info", otherInfoJSON);
      }

      const res = await sendFormDataPostRequest({
        link: "add-user",
        formData,
      });

      if (!res) {
        setError("Ошибка сервера");
      } else if (!res.success) {
        setError(res.message ?? "Не удалось создать пользователя");
      } else {
        const createdUser: DeanteUser = {
          login: res.user?.login ?? newLogin,
          discount:
            typeof res.user?.discount === "number"
              ? res.user.discount
              : safeDiscount,
          role: res.user?.role ?? newRole,
          other_info: otherInfoObj,
        };

        setSuccess(`Пользователь "${createdUser.login}" успешно создан`);

        if (onUserCreated) {
          onUserCreated(createdUser);
        }

        // Очистка формы
        setNewLogin("");
        setNewPassword("");
        setNewDiscount("0");
        setNewRole("user");

        setOtherInfoValues(
          OTHER_INFO_FIELDS.reduce(
            (acc, field) => ({ ...acc, [field.key]: "" }),
            {} as Record<string, string>
          )
        );
      }
    } catch (e: any) {
      console.error("error at add-user", e);
      setError("Проблема с сетью");
    } finally {
      setLoading(false);
    }
  };

  const [loading_utils, setLoading_utils] = useState(false)

  return (
    <VStack space={4} mt={4}>
      <Text fontSize="md" fontWeight="semibold">
        Администрирование: создание пользователя
      </Text>

      {/* Логин */}
      <Input
        placeholder="Логин"
        value={newLogin}
        onChangeText={setNewLogin}
        autoCapitalize="none"
      />

      {/* Пароль */}
      <Input
        placeholder="Пароль"
        value={newPassword}
        onChangeText={setNewPassword}
        type={showNewPassword ? "text" : "password"}
        InputRightElement={
          <Pressable onPress={() => setShowNewPassword((prev) => !prev)}>
            {showNewPassword ? (
              <EyeOff size={20} color="#6A6A6A" style={{ marginRight: 12 }} />
            ) : (
              <Eye size={20} color="#6A6A6A" style={{ marginRight: 12 }} />
            )}
          </Pressable>
        }
      />

      {/* Скидка + Роль */}
      <HStack space={3}>
        <Input
          flex={1}
          placeholder="Скидка, %"
          keyboardType="numeric"
          value={newDiscount}
          onChangeText={setNewDiscount}
        />

        <PlatformSelect
          flex={1}
          selectedValue={newRole}
          minWidth={150}
          accessibilityLabel="Роль"
          placeholder="Роль"
          _selectedItem={{
            endIcon: <CheckIcon size={4} />,
          }}
          onValueChange={(itemValue) =>
            setNewRole(itemValue as "user" | "admin")
          }
        >
          <NBSelect.Item label="User" value="user" />
          <NBSelect.Item label="Admin" value="admin" />
        </PlatformSelect>
      </HStack>

      {/* Статичный набор полей other_info */}
      <VStack space={3} mt={2}>

        <HStack justifyContent="space-between" alignItems="center" mb={2}>
                <Text fontSize="sm" fontWeight="medium">
                  Дополнительная информация (other_info)
                </Text>
        
                <Button
                  size="sm"
                  variant="outline"
                  onPress={async () => {
                    try{
                        setLoading_utils(true)
                        await getUtils()
                    }
                    catch(e){
                        console.error('error at refresh utils', e)
                    }finally{
                        setLoading_utils(false)
                    }
                  }}
                  isLoading={loading_utils}
                >
                  Обновить
                </Button>
              </HStack>

        

        {OTHER_INFO_FIELDS.map((field) => (
          <Input
            key={field.key}
            placeholder={field.placeholder ?? field.label}
            value={otherInfoValues[field.key] ?? ""}
            onChangeText={(text) => handleChangeOtherInfo(field.key, text)}
          />
        ))}
      </VStack>

      {error && (
        <Text color="red.500" fontSize="xs">
          {error}
        </Text>
      )}

      {success && (
        <Text color="green.500" fontSize="xs">
          {success}
        </Text>
      )}

      <Button onPress={handleCreateUser} isLoading={loading}>
        Создать пользователя
      </Button>
    </VStack>
  );
};

export default AdminCreateUser;
